/**
 * Textarea Component
 * Multi-line text input with validation and character count
 */

import { forwardRef, useId } from 'react';
import type { TextareaProps } from '../types/components';
import { cn } from '../utils/cn';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      name,
      label,
      error,
      required,
      hint,
      value,
      onChange,
      placeholder,
      rows = 4,
      maxLength,
      disabled,
      resize = 'vertical',
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const id = `textarea-${generatedId}`;
    const hasError = Boolean(error);
    const characterCount = value?.length || 0;

    const resizeStyles: Record<string, string> = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-300"
          >
            {label}
            {required && <span className="text-gray-500 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={id}
          name={name}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          required={required}
          className={cn(
            'block w-full rounded border bg-white px-3 py-2 text-base dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500',
            'transition-colors min-h-[80px]',
            'focus:outline-none focus:ring-2 focus:ring-offset-0 dark:focus:ring-slate-400 dark:focus:border-slate-400',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-slate-800 dark:disabled:text-slate-500',
            hasError
              ? 'border-gray-400 focus:border-gray-900 focus:ring-gray-200 dark:border-red-500/70'
              : 'border-gray-300 focus:border-gray-900 focus:ring-gray-100 dark:border-slate-700',
            resizeStyles[resize]
          )}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${id}-error` : hint ? `${id}-hint` : undefined
          }
          {...rest}
        />

        <div className="flex justify-between mt-1">
          <div>
            {error && (
              <p id={`${id}-error`} className="text-sm text-gray-500 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
            {hint && !error && (
              <p id={`${id}-hint`} className="text-sm text-gray-500 dark:text-slate-400">
                {hint}
              </p>
            )}
          </div>

          {maxLength && (
            <span
              className={cn(
                'text-sm',
                characterCount >= maxLength ? 'text-gray-500 dark:text-red-400' : 'text-gray-500 dark:text-slate-400'
              )}
            >
              {characterCount}/{maxLength}
            </span>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
